import { useEffect, useRef } from "react";
import type { BetaModule } from "@/shared/config/betaModules";
import { prefersReducedMotion } from "@/lib/motion";

type HurricaneItem = {
  element: HTMLDivElement;
  laneY: number;
  horizontalAmplitude: number;
  horizontalSpeed: number;
  phase: number;
  drift: number;
  rotationBias: number;
  chaos: number;
};

function hash01(id: string, salt: number): number {
  let hash = salt;
  for (let index = 0; index < id.length; index += 1) {
    hash = (Math.imul(hash, 31) + id.charCodeAt(index)) | 0;
  }
  return ((hash >>> 0) % 10000) / 10000;
}

export function useIconField(modules: BetaModule[]) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const items: HurricaneItem[] = modules.map((mod, index) => {
      const r0 = hash01(mod.id, 11);
      const r1 = hash01(mod.id, 23);
      const r2 = hash01(mod.id, 37);
      const r3 = hash01(mod.id, 53);
      const r4 = hash01(mod.id, 71);
      const element = document.createElement("div");
      element.className = "icon-field__item";
      element.title = mod.name;

      if (mod.icon) {
        element.innerHTML = `<img src="${mod.icon}" alt="" />`;
      } else {
        element.classList.add("is-placeholder");
        element.textContent = mod.label.slice(0, 2).toUpperCase();
      }
      container.appendChild(element);

      return {
        element,
        laneY: 50 + (r0 - 0.5) * 26 + (index % 4) * 0.8,
        horizontalAmplitude: 28 + r1 * 14,
        horizontalSpeed: 0.26 + r2 * 0.34,
        phase: r3 * Math.PI * 2 + index * 0.83,
        drift: 2 + r1 * 4,
        rotationBias: (r2 - 0.5) * 24,
        chaos: 0.6 + r4 * 1.4,
      };
    });

    const draw = (time: number) => {
      const frames = items.map((item) => {
        const t = time * item.horizontalSpeed;
        const depth = Math.sin(t + item.phase);
        const depthT = (depth + 1) / 2;
        const sweep = Math.sin(t * 0.9 + item.phase);
        const cross = Math.sin(t * 1.35 + item.phase * 1.7) * item.chaos;
        return {
          item,
          depth,
          x: 50 + sweep * item.horizontalAmplitude + cross * 3.5,
          y:
            item.laneY +
            Math.sin(time * 0.44 + item.phase * 1.2) * item.drift +
            depth * 1.8,
          scale: 0.94 * (0.52 + depthT * 0.58),
          opacity: 0.48 * (0.28 + depthT * 0.72),
          rotation:
            item.rotationBias + depth * 10 + Math.sin(time * 0.55 + item.phase) * 8,
        };
      });

      frames.sort((a, b) => a.depth - b.depth);
      frames.forEach((frameState) => {
        const { element } = frameState.item;
        element.style.left = `${frameState.x}%`;
        element.style.top = `${frameState.y}%`;
        element.style.opacity = String(frameState.opacity);
        element.style.zIndex = String(Math.round((frameState.depth + 1) * 12));
        element.style.transform =
          `translate3d(-50%, -50%, 0) rotate(${frameState.rotation}deg) scale(${frameState.scale})`;
      });
    };

    let animationFrame = 0;
    if (prefersReducedMotion()) {
      draw(0);
    } else {
      const animate = (now: number) => {
        draw(now * 0.001);
        animationFrame = requestAnimationFrame(animate);
      };
      animationFrame = requestAnimationFrame(animate);
    }

    return () => {
      cancelAnimationFrame(animationFrame);
      items.forEach(({ element }) => element.remove());
    };
  }, [modules]);

  return containerRef;
}
